import { default as React } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography,
} from '@material-ui/core';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
  },
  sectionHeader: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: '1.1rem',
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1),
  },
  sectionDescription: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
  },
  divider: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  label: {
    fontWeight: 600,
    fontSize: '1rem',
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary,
  },
  radioGroup: {
    flexDirection: 'row',
    gap: theme.spacing(4),
  },
  radioLabel: {
    '& .MuiFormControlLabel-label': {
      fontSize: '0.95rem',
      fontWeight: 500,
    },
  },
  select: {
    minWidth: '250px',
  },
  helperText: {
    marginTop: theme.spacing(1),
    fontSize: '0.875rem',
  },
  errorText: {
    color: theme.palette.error.main,
  },
  switchSection: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.type === 'dark' ? '#2a2a2a' : '#f5f5f5',
    borderRadius: theme.shape.borderRadius,
  },
  switchLabel: {
    fontSize: '0.95rem',
    fontWeight: 500,
  },
  switchHelper: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(4),
    marginTop: theme.spacing(0.5),
  },
}));

interface DotnetApiOptionsData {
  dotnet_version?: string;
  api_style?: string;
  auth_type?: string;
  include_docker?: boolean;
  include_swagger?: boolean;
  include_health_checks?: boolean;
}

const DOTNET_VERSIONS = [
  { value: '6.0', label: '.NET 6.0' },
  { value: '7.0', label: '.NET 7.0' },
  { value: '8.0', label: '.NET 8.0' },
];

const API_STYLES = [
  { value: 'minimal', label: 'Minimal API' },
  { value: 'controller', label: 'Controller-based' },
];

const AUTH_TYPES = [
  { value: 'none', label: 'None' },
  { value: 'jwt', label: 'JWT Bearer' },
  { value: 'oauth2', label: 'OAuth 2.0' },
  { value: 'apikey', label: 'API Key' },
];

export const DotnetApiOptions = ({
  onChange,
  formData = {},
  rawErrors,
  schema,
}: FieldExtensionComponentProps<DotnetApiOptionsData>) => {
  const classes = useStyles();
  const hasErrors = rawErrors && rawErrors.length > 0;

  const data: DotnetApiOptionsData = {
    dotnet_version: formData.dotnet_version || '8.0',
    api_style: formData.api_style || 'minimal',
    auth_type: formData.auth_type || 'none',
    include_docker:
      formData.include_docker !== undefined ? formData.include_docker : true,
    include_swagger:
      formData.include_swagger !== undefined ? formData.include_swagger : true,
    include_health_checks:
      formData.include_health_checks !== undefined
        ? formData.include_health_checks
        : true,
  };

  const handleFieldChange = (field: keyof DotnetApiOptionsData, value: any) => {
    onChange({ ...data, [field]: value });
  };

  return (
    <Box className={classes.root}>
      {/* Section Header */}
      <Box className={classes.sectionHeader}>
        <Typography className={classes.sectionTitle}>
          .NET API Configuration
        </Typography>
        <Typography className={classes.sectionDescription}>
          Configure your .NET API template with version, style, and
          authentication options
        </Typography>
      </Box>

      <Divider className={classes.divider} />

      {/* .NET Version Selection */}
      <FormControl fullWidth className={classes.section}>
        <FormLabel className={classes.label}>.NET Version</FormLabel>
        <Select
          value={data.dotnet_version}
          onChange={e => handleFieldChange('dotnet_version', e.target.value)}
          variant="outlined"
          className={classes.select}
        >
          {DOTNET_VERSIONS.map(version => (
            <MenuItem key={version.value} value={version.value}>
              {version.label}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText className={classes.helperText}>
          Select the .NET framework version for your API
        </FormHelperText>
      </FormControl>

      {/* API Style Selection */}
      <FormControl fullWidth className={classes.section}>
        <FormLabel className={classes.label}>API Style</FormLabel>
        <RadioGroup
          value={data.api_style}
          onChange={e => handleFieldChange('api_style', e.target.value)}
          className={classes.radioGroup}
        >
          {API_STYLES.map(style => (
            <FormControlLabel
              key={style.value}
              value={style.value}
              control={<Radio color="primary" />}
              label={style.label}
              className={classes.radioLabel}
            />
          ))}
        </RadioGroup>
        <FormHelperText className={classes.helperText}>
          Choose between Minimal API (modern, concise) or Controller-based
          (traditional, structured)
        </FormHelperText>
      </FormControl>

      {/* Authentication Type Selection */}
      <FormControl fullWidth className={classes.section}>
        <FormLabel className={classes.label}>Authentication Type</FormLabel>
        <Select
          value={data.auth_type}
          onChange={e => handleFieldChange('auth_type', e.target.value)}
          variant="outlined"
          className={classes.select}
        >
          {AUTH_TYPES.map(auth => (
            <MenuItem key={auth.value} value={auth.value}>
              {auth.label}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText className={classes.helperText}>
          Select the authentication mechanism for your API
        </FormHelperText>
      </FormControl>

      <Divider className={classes.divider} />

      {/* Additional Options (Switches) */}
      <Box className={classes.switchSection}>
        <Typography variant="h6" className={classes.label}>
          Additional Options
        </Typography>

        <Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={data.include_docker}
                onChange={e =>
                  handleFieldChange('include_docker', e.target.checked)
                }
                color="primary"
              />
            }
            label="Include Docker Support"
            className={classes.switchLabel}
          />
          <Typography className={classes.switchHelper}>
            Generate Dockerfile and .dockerignore for containerization
          </Typography>
        </Box>

        <Box mt={2}>
          <FormControlLabel
            control={
              <Checkbox
                checked={data.include_swagger}
                onChange={e =>
                  handleFieldChange('include_swagger', e.target.checked)
                }
                color="primary"
              />
            }
            label="Include Swagger/OpenAPI"
            className={classes.switchLabel}
          />
          <Typography className={classes.switchHelper}>
            Add Swagger UI and OpenAPI documentation for API exploration
          </Typography>
        </Box>

        <Box mt={2}>
          <FormControlLabel
            control={
              <Checkbox
                checked={data.include_health_checks}
                onChange={e =>
                  handleFieldChange('include_health_checks', e.target.checked)
                }
                color="primary"
              />
            }
            label="Include Health Checks"
            className={classes.switchLabel}
          />
          <Typography className={classes.switchHelper}>
            Add health check endpoints for monitoring and readiness probes
          </Typography>
        </Box>
      </Box>

      {/* Error Display */}
      {hasErrors && (
        <FormHelperText className={classes.errorText}>
          {rawErrors?.join(', ')}
        </FormHelperText>
      )}

      {/* Schema Description */}
      {!hasErrors && schema.description && (
        <FormHelperText className={classes.helperText}>
          {schema.description}
        </FormHelperText>
      )}
    </Box>
  );
};

export const DotnetApiOptionsExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'DotnetApiOptions',
    component: DotnetApiOptions,
  }),
);
